'use client';
import Checkbox from '@/shared/ui/Checkbox/Checkbox';
import { FC, useState, useMemo, useCallback, ChangeEvent } from 'react';
import {
  cvaGroup,
  cvaGroups,
  cvaGroupTitle,
  cvaItems,
  cvaRoot,
  cvaSearch,
  cvaSearchIcon,
  cvaSearchInput,
  cvaShowMore,
  cvaShowMoreIcon,
  cvaShowMoreText,
} from './CheckboxesGroupStyles';
import IconCaret from '/public/icons/arrow-down.svg';
import IconSearch from '/public/icons/search3.svg';
import { useFiltersStore } from '@/shared/store/filtersStore';

interface Props {
  searchTitle: string;
  name: string;
  type?: 'checkbox' | 'radio';
  items: {
    id: number;
    title: string;
    value: string;
  }[];
  fullItems?: {
    id: number;
    group: string;
    items: {
      id: number;
      title: string;
      value: string;
    }[];
  }[];
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const CheckboxesGroup: FC<Props> = ({
  searchTitle,
  name,
  items: originItems,
  fullItems: originFullItems = [],
  type = 'checkbox',
  onChange,
}) => {
  const [search, setSearch] = useState('');
  const onSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value), []);

  const items = useMemo(() => {
    const substr = search.trim().toLowerCase();

    return originItems.filter(({ title }) => {
      const str = title.trim().toLowerCase();
      return str.includes(substr);
    });
  }, [originItems, search]);

  const fullItems = useMemo(() => {
    const substr = search.trim().toLowerCase();

    return originFullItems
      .map((item) => ({
        ...item,
        items: item.items.filter(({ title }) => {
          const str = title.trim().toLowerCase();
          return str.includes(substr);
        }),
      }))
      .filter(({ items }) => items.length);
  }, [originFullItems, search]);

  const [showHidden, setShowHidden] = useState(false);
  const [splicedItems, setSplicedItems] = useState(5);

  const filterState = useFiltersStore((state) => state.filters);
  const shop_ids = useFiltersStore((state) => state.shop_ids);

  const checkedItems = useMemo(() => {
    if (name === 'SHOPS') {
      return (shop_ids || []).map((val) => `${val}`);
    }
    return filterState[name] || [];
  }, [name, filterState, shop_ids]);

  const checkedItemsFull = items.filter((item) => checkedItems.includes(String(item.value)));

  const handleShowMore = () => {
    if (!fullItems?.length) {
      if (showHidden) {
        setSplicedItems(5);
      } else {
        setSplicedItems(items.length);
      }
    }

    setShowHidden((prev) => !prev);
  };

  return (
    <div className={cvaRoot()}>
      {searchTitle?.length ? (
        <div className={cvaSearch()}>
          <input
            type="text"
            className={cvaSearchInput()}
            placeholder={searchTitle}
            onChange={onSearch}
            value={search}
          />
          <IconSearch className={cvaSearchIcon()} />
        </div>
      ) : null}
      {!fullItems?.length || (fullItems?.length && !showHidden) ? (
        <div className={cvaItems({ scroll: true })}>
          {checkedItemsFull?.map((item) => {
            return (
              <Checkbox
                key={item.id}
                type={'checkbox'}
                name={name}
                value={item.value}
                title={item.title}
                onChange={onChange}
                checked={checkedItems.includes(String(item.value))}
              />
            );
          })}
          {[...items].splice(0, splicedItems)?.map((item) => {
            if (checkedItems.includes(String(item.value))) {
              return null;
            }

            return (
              <Checkbox
                key={item.id}
                type={'checkbox'}
                name={name}
                value={item.value}
                title={item.title}
                onChange={onChange}
                checked={checkedItems.includes(String(item.value))}
              />
            );
          })}
        </div>
      ) : (
        <div className={cvaGroups()}>
          {checkedItemsFull?.map((item) => {
            return (
              <Checkbox
                key={item.id}
                type={'checkbox'}
                name={name}
                value={item.value}
                title={item.title}
                onChange={onChange}
                checked={checkedItems.includes(String(item.value))}
              />
            );
          })}
          {fullItems?.map((group) => {
            return (
              <div key={group.id} className={cvaGroup()}>
                <div className={cvaGroupTitle()}>{group.group}</div>
                <div className={cvaItems()}>
                  {[...group.items].map((item) => {
                    if (checkedItems.includes(String(item.value))) {
                      return null;
                    }

                    return (
                      <Checkbox
                        key={item.id}
                        type={type}
                        name={name}
                        value={item.value}
                        title={item.title}
                        onChange={onChange}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {items?.length > 5 || fullItems?.length ? (
        <div className={cvaShowMore()} onClick={() => handleShowMore()}>
          <div className={cvaShowMoreText()}>{showHidden ? 'Свернуть' : 'Развернуть'}</div>
          <IconCaret
            className={cvaShowMoreIcon({
              active: showHidden,
            })}
          />
        </div>
      ) : null}
    </div>
  );
};

export default CheckboxesGroup;
